import { produce, WritableDraft } from "immer";
import { HexMap } from "./HexMap";
import { All } from "../../Util/Constants/Quantities";
import { KeyRelationship, Note } from "./Note";
import { Key } from "./Key";
import { Modes } from "../../Util/Music/Modes";
import { Music } from "../../Util/Music/Music";

export class HexMapModifier{
    draft:WritableDraft<HexMap>
    setter:Function
    constructor(draft:WritableDraft<HexMap>, setter:Function = ()=>{}){
        this.draft = draft
        this.setter = setter
    }
    setDraft(draft:WritableDraft<HexMap>){this.draft = draft}
    setTonic(tonic:number){
        const currTonic:number = this.draft.keys.flat()[0].params.tonic
        const delta:number = tonic - currTonic
        this.notes_cycle([All], (note:Note)=>{note.params.tonic = Music.addInterval(note.params.tonic, delta)})
        this.keys_cycle([All], (key:Key)=>{key.params.tonic = Music.addInterval(key.params.tonic, delta)})
    }
    note_getKeyRelationship(noteIndex:number, position:number){
        const note:Note = this.draft.notes[noteIndex]
        const relationship:KeyRelationship|undefined = note.keys.find((relationship:KeyRelationship)=>{
            return relationship.position === position
        })
        return relationship
    }
    note_setKeyLabel(noteIndex:number, position:number, value:string|undefined = undefined ){
        const label:string = (value === undefined) ? Modes.Names[position] : value
        const relationship:KeyRelationship|undefined = this.note_getKeyRelationship(noteIndex, position)
        if(relationship){relationship.label = label; relationship.labelDisplay = true}
    }
    note_setKeyLabelDisplay(noteIndex:number, position:number, display:boolean){
        const relationship:KeyRelationship|undefined = this.note_getKeyRelationship(noteIndex, position)
        if(relationship){relationship.labelDisplay = display}
    }
    note_setKeyPetalDisplay(noteIndex:number, position:number, display:boolean){
        const relationship:KeyRelationship|undefined = this.note_getKeyRelationship(noteIndex, position)
        if(relationship){relationship.petalDisplay = display}
    }
    notes_getAllIndexes():number[]{
        return this.draft.notes.map((note:Note)=>{return note.params.noteIndex})
    }
    notes_getModIndexes(indexes:number[]){
        if(indexes[0] === All){ return this.notes_getAllIndexes() }
        return indexes
    }
    notes_cycle(indexes:number[], modifier:Function){
        const noteIndexes:number[] = this.notes_getModIndexes(indexes)
        noteIndexes.forEach((noteIndex:number)=>{
            modifier(this.draft.notes[noteIndex])
        })
    } 
    notes_setDefined(indexes:number[], defined:boolean, otherwise?:boolean){
        if(otherwise){this.notes_cycle([All], (note:Note)=>{note.defined = !defined})}
        this.notes_cycle(indexes, (note:Note)=>{note.defined = defined})
    } 
    notes_setVisible(indexes:number[], visible:boolean){
        this.notes_cycle(indexes, (note:Note)=>{note.visible = visible})
    }  
    notes_setPrimaryLabel(indexes:number[], label:string|null){
        this.notes_cycle(indexes, (note:Note)=>{note.primaryLabel = label})
    }
    notes_setPrimaryLabelAsPosition(indexes:number[], offset?:number){
        const adjustedOffset:number = (offset) ? offset : 0
        this.notes_cycle(indexes, (note:Note)=>{
            const label:string = String((note.params.noteIndex - adjustedOffset + 7) % 7  + 1)

            note.primaryLabel = String(label)
        })
    }  
    notes_setPrimaryLabelAsNoteIndex(indexes:number[]){
        this.notes_cycle(indexes, (note:Note)=>{
            const label:string = String(note.params.noteIndex)
            note.primaryLabel = String(label)
        })
    } 
    notes_setSecondaryLabel(indexes:number[], label:string|null){
        this.notes_cycle(indexes, (note:Note)=>{note.secondaryLabel = label})
    }    
    notes_setSelectedState(indexes:number[], selectState:number){
        this.notes_cycle(indexes, (note:Note)=>{note.selectedState = selectState})
    }
    notes_setHilited(indexes:number[]){
        this.notes_cycle(indexes, (note:Note)=>{note.selectedState = Note.SelectedState_Hilited})
    }
    notes_setSelected(indexes:number[]){
        this.notes_cycle(indexes, (note:Note)=>{note.selectedState = Note.SelectedState_Selected})
    }
    notes_setDefault(indexes:number[]){
        this.notes_cycle(indexes, (note:Note)=>{note.selectedState = Note.SelectedState_Default})
    }
    notes_advanceSelectedState(indexes:number[]){
        this.notes_cycle(indexes, (note:Note)=>{
            note.selectedState = ((note.selectedState + 1) % 3)}
        )
    }
    notes_setHalo(indexes:number[], on:boolean){
        this.notes_cycle(indexes, (note:Note)=>{
            note.halo = on
        })
    }
    notes_removeClasses(indexes:number[], classNames:string[]){
        const removeClass = (note:Note) => {
            classNames.forEach((className:string)=>{
                const i:number = note.classNames.indexOf(className)
                if(i >= 0){ note.classNames.splice(i,1)}
            })
        }
        this.notes_cycle(indexes, removeClass)
    }
    notes_addClasses = (indexes:number[], classNames:string[]) => {
        const addClass = (note:Note) => {
            classNames.forEach((className:string)=>{
                const i:number = note.classNames.indexOf(className)
                if(i < 0){ note.classNames.push(className)}
            })
        }
        this.notes_cycle(indexes, addClass)
    }
    notes_toggleClasses(indexes:number[], classNames:string[]){
        const noteIndexes:number[] = this.notes_getModIndexes(indexes)
        const toggleClasses = (note:Note) => {
            classNames.forEach((className:string)=>{
                const i:number = note.classNames.indexOf(className)
                if(i >= 0){ note.classNames.splice(i,1)}
                else{
                    note.classNames = note.classNames.concat([className])
                }
            })   
        }
        this.notes_cycle(noteIndexes, toggleClasses)
    }

    key_getAllIndexes():number[]{
        return this.draft.keys.flat().map((key:Key)=>{return key.params.keyIndex})
    }
    key_getModIndexes(indexes:number[]){
        if(indexes[0] === All){ return this.key_getAllIndexes() }
        return indexes
    }
    keys_cycle(indexes:number[], modifier:Function){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        keyIndexes.forEach((keyIndex:number)=>{
            modifier(this.draft.keys.flat()[keyIndex])
        })
    } 
    keys_setDefined(indexes:number[], defined:boolean){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        const setDefined = (key:Key)=>{key.defined = defined}
        this.keys_cycle(keyIndexes, setDefined)
    } 
    keys_setPredefinedLabel(indexes:number[], label:string){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        this.keys_cycle(keyIndexes, (key:Key)=>{key.predefinedLabel = label})
    }
    keys_setSecondaryLabel(indexes:number[], label:string){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        this.keys_cycle(keyIndexes, (key:Key)=>{key.secondaryLabel = label})
    }
    keys_setPositionAsLabel(indexes:number[]){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        const setDefined = (key:Key)=>{key.predefinedLabel = String(key.params.keyIndex)}
        this.keys_cycle(keyIndexes, setDefined)
    } 
    keys_setVisible(indexes:number[], visible:boolean){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        this.keys_cycle(keyIndexes, (key:Key)=>{key.visible = visible})
    }     
    keys_setSelectedState(indexes:number[], selectState:number){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        this.keys_cycle(keyIndexes, (key:Key)=>{key.selectedState = selectState})
    }  
    keys_setHilited(indexes:number[]){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        this.keys_cycle(keyIndexes, (key:Key)=>{key.selectedState = Note.SelectedState_Hilited})
    }
    keys_setSelected(indexes:number[]){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        this.keys_cycle(keyIndexes, (key:Key)=>{key.selectedState = Note.SelectedState_Selected})
    }
    keys_setDefault(indexes:number[]){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        this.keys_cycle(keyIndexes, (key:Key)=>{key.selectedState = Note.SelectedState_Default})
    }   
    keys_advanceSelectedState(indexes:number[]){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        this.keys_cycle(keyIndexes, (key:Key)=>{key.selectedState = ((key.selectedState + 1) % 3)})
    }
    keys_removeClasses(indexes:number[], classNames:string[]){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        const removeClasses = (key:Key) => {
            classNames.forEach((className:string)=>{
                const i:number = key.classNames.indexOf(className)
                if(i >= 0){ key.classNames.splice(i,1)}
            })
        }
        this.keys_cycle(keyIndexes, removeClasses)
    }
    keys_toggleClasses(indexes:number[], classNames:string[]){
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        const toggleClasses = (key:Key) => {
            classNames.forEach((className:string)=>{
                const i:number = key.classNames.indexOf(className)
                if(i >= 0){ key.classNames.splice(i,1)}
                else{
                    key.classNames = key.classNames.concat([className])
                }
            })   
        }
        this.keys_cycle(keyIndexes, toggleClasses)
    }
    keys_addClasses = (indexes:number[], classNames:string[]) => {
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        const addClass = (key:Key) => {
            classNames.forEach((className:string)=>{
                const i:number = key.classNames.indexOf(className)
                if(i < 0){ key.classNames.push(className)}
            })
        }
        this.keys_cycle(keyIndexes, addClass)
    }
    keys_setOpen = (indexes:number[], toOpen:boolean) => {
        const keyIndexes:number[] = this.key_getModIndexes(indexes)
        const setOpen = (key:Key) => {
            if(!key.visible){return}
            key.open = true
            key.defined = false
            key.noteIndexes.forEach((noteIndex:number)=>{
                const note:Note = this.draft.notes[noteIndex]
                note.visible = true
                note.defined = true
            })
        }
        const setClosed = (key:Key) => {
            key.open = false
            key.defined = true
            key.noteIndexes.forEach((noteIndex:number)=>{
                if(!Note.hasAnotherOpenKey(noteIndex, key.params.keyIndex, this.draft)){
                    this.draft.notes[noteIndex].visible = false
                }
            })
        }
        const modifier = (toOpen) ? setOpen : setClosed
        this.keys_cycle(keyIndexes, modifier)
    }
    static onKeyClick(setter:Function, keyIndex:number){
        setter(produce((draft:WritableDraft<HexMap>)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.keys_advanceSelectedState([keyIndex])
        }))
    }
    static onNoteClick(setter:Function, noteIndex:number){
        setter(produce((draft:WritableDraft<HexMap>)=>{
            const mod:HexMapModifier = new HexMapModifier(draft)
            mod.notes_advanceSelectedState([noteIndex])
        }))
    }
}
